import { Component, DoCheck, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { ApiService } from 'src/app/core/api.service';
import { Key } from 'src/app/core/models/key';
import { Lesson } from 'src/app/core/models/lesson';
import { SystemService } from 'src/app/core/system.service';
import { KeylessonResultsDialogComponent } from 'src/app/shared/dialogs/keylesson-results-dialog/keylesson-results-dialog.component';

export class ExerciseState {
  userInput: string;
  typedKeys: { key: string, isCorrect: boolean }[] = [];
  nextKey: Key;
  strokes: number;
  errors: number;
  timeSpent;
  totalErrors: number = 0;
  totalStrokes: number = 0;
}

@Component({
  selector: 'app-keylesson-player',
  templateUrl: './keylesson-player.component.html',
  styleUrls: ['./keylesson-player.component.scss']
})
export class KeylessonPlayerComponent implements OnInit, DoCheck, OnDestroy {
  // carousel for keyboards
  exerciseChanged$ = new Subject<string>();

  lesson: Lesson;
  keyConfig: { keyboardKeys: Key[], allowedKeys: string[] };
  @ViewChild('videoPlayer') videoPlayer: ElementRef;

  // exercise index
  selectedExercise: number = 1;
  toContinueLesson: boolean;
  exerciseFromBookmark: number = 0;

  @ViewChild("charInput") charInputRef: ElementRef;
  userInput = new FormControl();
  state: ExerciseState = new ExerciseState();
  timer;

  showBookmarkMsg: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    public authService: AuthService,
    private systemService: SystemService,    
    public dialog: MatDialog) { }

  ngDoCheck() {
    if(this.charInputRef) {
      this.charInputRef.nativeElement.focus({
        preventScroll: true
      });
    }
  }

  ngOnInit() {
    // resolver data
    this.route.data
      .subscribe((value: {keys: any}) => {
          this.keyConfig = value.keys;
      });

    // check if its to continue or start over
    this.route.params
      .subscribe(value => {
        const toContinue = value.continue === 'true';
        this.toContinueLesson = toContinue

        const exerciseFromBookmark = value.exercise;
        this.exerciseFromBookmark = exerciseFromBookmark || 0;

        // if already in player , then we need to refresh
        if(!!this.lesson) {
          this.refresh();
        }

      })
    
    // parent lesson resolver
    this.route.parent.data
      .subscribe((value: {lesson: Lesson}) => {
        this.lesson = value.lesson

        this.refresh();
      });

    // user input
    this.userInput.valueChanges
      .subscribe((value: string) => {
        const isBackspace = value.length <= this.state.typedKeys.length;

        // reset state
        this.state.typedKeys = [];
        this.state.errors = 0;
        this.state.strokes = 0;

        const charactersTyped = value.split('');

        // update typed keys by user
        charactersTyped.forEach((value, index) => {
          const exerciseCharAt = this.exercise.charAt(index);
          this.state.typedKeys.push({
            key: value,
            isCorrect: value === exerciseCharAt            
          });
          this.state.strokes++
          this.state.errors += value !== exerciseCharAt  ? 1 : 0;
        })

        // check correctness of last char
        if(!isBackspace && this.state.typedKeys.length > 0 && !this.state.typedKeys[this.state.typedKeys.length - 1].isCorrect) {
          this.beep();
        }

        // next key

        // #1 - if we completed, the next key should be an space
        if(this.state.typedKeys.length === this.exercise.length) { 
          const nextChar = 'space';
          this.state.nextKey = this.keyConfig.keyboardKeys.find((value: Key) => value.character === nextChar);
        }

        // #2 - if user type more than exercise proceed for next
        if(this.state.typedKeys.length >= this.exercise.length) { 
          const { errors, strokes } = this.state;
          this.state.totalErrors += errors;
          this.state.totalStrokes += strokes;

          this.continueNextExercise();
        } 

        // #3 - check for next char
        if(this.state.typedKeys.length < this.exercise.length) { 
          let nextChar = this.exercise[this.state.typedKeys.length];
          
          // <space bar>
          if(nextChar === ' ') { nextChar = 'space' }
          
          this.state.nextKey = this.keyConfig.keyboardKeys.find((value: Key) => value.character === nextChar.toLowerCase());

          const useShift = this.state.nextKey.character.toUpperCase() === nextChar;// TODO       
          
        }       

      })

    this.exerciseChanged$
      .subscribe((value: string) => {
        if(value === 'left') { 
          if(this.selectedExercise === 1) { return false; }
          else { 
            this.selectedExercise--;
            
            // persist timer, errors and strokes throught navigation
            const { totalStrokes, totalErrors, strokes, errors, timeSpent } = this.state;
            this.state = {
              ...this.state,
              totalStrokes: totalStrokes + strokes,
              totalErrors: totalErrors + errors
            };
            this.setupExercise();
            this.state.timeSpent = timeSpent;
          }            
        }

        if(value === 'right') { 
          if(this.selectedExercise === this.lesson.exercises.length) { return false; }
          else { 
            this.selectedExercise++;

            // persist timer, errors and strokes throught navigation
            const { totalStrokes, totalErrors, strokes, errors, timeSpent } = this.state;              
            this.state = {
              ...this.state,
              totalStrokes: totalStrokes + strokes,
              totalErrors: totalErrors + errors
            };
            this.setupExercise();
            this.state.timeSpent = timeSpent;
          }
        }

        if(this.videoPlayer){
          this.videoPlayer.nativeElement.load();
        }
        
      })

  }

  ngOnDestroy() {
    if(!this.lesson) {return;}
    const {userid, language_in_use  } = this.authService.user$.value;

    let requests = [
      this.apiService.setBookmark(userid, this.lesson.id, this.selectedExercise, language_in_use)  
    ]

    if((this.state.totalStrokes + this.state.strokes )> 0 && this.state.timeSpent > 0) {
      const timeFormatted = new Date(this.state.timeSpent * 1000).toISOString().substr(11, 8)
      requests.push(
        this.apiService.sendResultsLesson(userid, this.lesson.id, (this.state.totalStrokes + this.state.strokes), (this.state.totalErrors + this.state.errors), timeFormatted, language_in_use)
      )
    }

    //send results to database
    forkJoin([...requests])
      .subscribe(value => {
        // reload system bookmarks
        this.systemService.reload$.next();      })

    // clear timer
    if(this.timer) {clearInterval(this.timer)}

  }

  refresh() {
    if(this.exerciseFromBookmark === 0) {
      // ckeck if is to continue or reset
      this.selectedExercise = this.toContinueLesson
      ? this.lesson.lastExercise || 1
      : 1;  
    }else {
      // go directly to bookmarked exercise
      this.selectedExercise = +this.exerciseFromBookmark;
    }

    if (this.selectedExercise > this.lesson.exercises.length) {this.selectedExercise = 1}

    // setup exercise             
    this.setupExercise();
  }

  setupExercise() {
    const firstChar = this.exercise[0];
    this.state.nextKey = this.keyConfig.keyboardKeys.find((value: Key) => value.character === firstChar.toLowerCase());
    this.state.typedKeys = [];
    this.state.strokes = 0;
    this.state.errors = 0;
    this.state.timeSpent = 0;
    this.userInput.setValue('');
    this.startTimer();
  }

  continueNextExercise() {
    if(this.selectedExercise === this.lesson.exercises.length){
      // mark as completed
      const {userid,language_in_use } = this.authService.user$.value;

      this.apiService.markLessonCompleted(userid, this.lesson.id, language_in_use)
        .subscribe(_ => {
          console.log('Lesson finished, reloading...')
          this.systemService.reload$.next();
        })
      return this.finishGame();
    }

    this.selectedExercise++;

    const firstChar = this.exercise[0];
    this.state.nextKey = this.keyConfig.keyboardKeys.find((value: Key) => value.character === firstChar.toLowerCase());
    this.state.typedKeys = [];
    this.userInput.setValue('');
  }

  resetUserInput() {
    this.userInput.setValue('');
  }

  startTimer() {
    if(this.timer) { this.pauseTimer(); this.timer = undefined}
    this.timer = setInterval(() => {
      this.state.timeSpent++
    },1000)
  }

  pauseTimer() {
    clearInterval(this.timer);
  }

  finishGame() {
    this.pauseTimer();
    this.openResultDialog();
  }

  openResultDialog(): void {
    const dialogRef = this.dialog.open(KeylessonResultsDialogComponent, {      
      width: '50vw',
      data: {
        lesson: this.lesson,
        state: this.state
      }
    });

    dialogRef.afterClosed()
      .subscribe(_ => {
        this.router.navigate(['/keylesson']);
      });
  }

  setNewBookmark(){
    const {userid, language_in_use} = this.authService.user$.value;
    
    this.apiService.setBookmark(userid, this.lesson.id, this.selectedExercise, language_in_use)
      .pipe(
        tap(_ => {
          this.showBookmarkMsg = true;
          setTimeout(() => {
            this.showBookmarkMsg = false;
          }, 5000);
        })
      )
      .subscribe(_ => this.systemService.reload$.next())
  }

  get allowedKeys(): Key[]{
    if(!this.keyConfig) { return [] }

    return this.keyConfig.keyboardKeys.filter((value: Key) => this.keyConfig.allowedKeys.find(allowedKey => allowedKey === value.character));
  }

  get exercise(): string {
    if(!this.lesson) { return '' }

    return this.lesson.exercises[this.selectedExercise - 1];
  }

  getCaseNextKey(): string {
    let nextChar = this.exercise[this.state.typedKeys.length];

    this.state.nextKey = this.keyConfig.keyboardKeys.find((value: Key) => value.character === nextChar.toLowerCase());

    const useShift = this.state.nextKey.character.toUpperCase() === nextChar;       

    return useShift
      ? this.state.nextKey.character.toUpperCase()
      : this.state.nextKey.character;
  }

  isNextKey(key: Key): boolean {
    if(!this.state.nextKey) { return false }
    return this.state.nextKey.character === key.character.toLowerCase();
  }

  get exerciseVideoUrl(): string {

    const {language_in_use} = this.authService.user$.value;

    const video = this.lesson.videos.get(this.selectedExercise);
    if(video === 'x') { return ''; }

    //return 'assets/videos/Lesson 1.mp4'
    return `https://platform.keyboardpro.com/lang/${language_in_use}/kbp/${video}`;
  }

  beep() {
    var snd = new  Audio("data:audio/wav;base64,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");  
    snd.play();
  }

}
